import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { FeedbackReport } from "@/lib/tcf";
import { Mic, Square, Clock, Loader2, ArrowLeft, RotateCcw, ShieldCheck, Volume2, Type } from "lucide-react";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export default function SpeakingLab() {
  const [tasks, setTasks] = useState([]);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [customPrompt, setCustomPrompt] = useState("");

  const timerRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => { api.get("/tcf/speaking-tasks").then((r) => setTasks(r.data)).catch((e) => console.error("tasks", e)); }, []);

  useEffect(() => {
    if (recording) {
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          const next = s + 1;
          if (active && next >= active.seconds) stopRec();
          return next;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording, active]);

  useEffect(() => () => { streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);

  const open = (t) => { setActive(t); setText(""); setSeconds(0); setResult(null); setRecording(false); setCustomPrompt(t.prompt || ""); };
  const isCustomTask = active && (active.id === "so_t2" || active.id === "so_t3");

  const startRec = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Votre navigateur ne supporte pas l'enregistrement. Tapez votre réponse."); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        await transcribe(blob);
      };
      mediaRef.current = mr;
      setSeconds(0);
      mr.start();
      setRecording(true);
    } catch { toast.error("Accès au micro refusé."); }
  };

  const stopRec = () => { if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop(); setRecording(false); };

  const transcribe = async (blob) => {
    if (blob.size < 800) { toast.info("Rien entendu — réessayez."); return; }
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "answer.webm");
      fd.append("language", "fr");
      const { data } = await api.post("/speech/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setText((prev) => (prev ? prev + " " : "") + (data.transcript || ""));
      if (!data.transcript) toast.info("Transcription vide — parlez un peu plus fort.");
    } catch (e) { toast.error(e.response?.data?.detail || "Transcription impossible."); }
    finally { setTranscribing(false); }
  };

  const words = text.trim() ? text.trim().split(/\s+/).length : 0;

  const submit = async () => {
    if (words < 10) { toast.error("Parlez ou écrivez au moins quelques phrases."); return; }
    setLoading(true);
    try {
      const payload = { task_id: active.id, transcript: text };
      if (isCustomTask && customPrompt.trim()) payload.custom_prompt = customPrompt.trim();
      const { data } = await api.post("/tcf/evaluate-speaking", payload);
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Évaluation impossible.");
    } finally { setLoading(false); }
  };

  if (result) {
    return (
      <div data-testid="speaking-lab-result">
        <button onClick={() => { setActive(null); setResult(null); }} className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="h-4 w-4" /> Back to tasks</button>
        <div className="flex items-center justify-between mb-5">
          <h1 className="font-display text-3xl font-semibold">Your feedback</h1>
          <button onClick={() => open(active)} data-testid="sl-retry" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold"><RotateCcw className="h-4 w-4" /> Try again</button>
        </div>
        <FeedbackReport result={result} userText={text} />
      </div>
    );
  }

  if (active) {
    return (
      <div data-testid="speaking-lab-recorder">
        <button onClick={() => { stopRec(); setActive(null); }} className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="h-4 w-4" /> Back to tasks</button>
        <div className="rounded-2xl bg-card border border-border p-6">
          <div className="flex items-center gap-2 text-xs font-semibold">
            <span className="px-2.5 py-1 rounded-full bg-accent text-accent-foreground">{active.target}</span>
            <span className="px-2.5 py-1 rounded-full bg-secondary">~{Math.round(active.seconds / 60 * 10) / 10} min</span>
          </div>
          <h1 className="font-display text-2xl font-semibold mt-4">{active.title}</h1>
          {isCustomTask ? (
            <div className="mt-3">
              <label className="text-xs font-semibold text-muted-foreground flex items-center gap-2 mb-1">
                <Type className="h-3.5 w-3.5" /> {active.id === "so_t2" ? "Your Tâche 2 situation — what do you need to ask about? (edit or write your own)" : "Your Tâche 3 question — the opinion you'll defend (edit or write your own)"}
              </label>
              <textarea data-testid="sl-custom-prompt" value={customPrompt} onChange={(e) => setCustomPrompt(e.target.value)}
                className="w-full min-h-[80px] p-3 rounded-xl bg-background border border-border focus:outline-none focus:ring-2 focus:ring-ring leading-relaxed resize-y text-sm" />
              <p className="text-[11px] text-muted-foreground mt-1">We'll evaluate your recorded answer against this question and show it rewritten at B1 and B2.</p>
            </div>
          ) : (
            <p className="text-muted-foreground mt-2 leading-relaxed">{active.prompt}</p>
          )}
        </div>

        <div className="mt-6 rounded-2xl bg-card border border-border p-6 text-center">
          <div className="inline-flex items-center gap-2 text-sm font-semibold mb-4"><Clock className="h-4 w-4 text-primary" /> {fmt(seconds)} / {fmt(active.seconds)}</div>
          <div>
            {!recording ? (
              <button onClick={startRec} disabled={transcribing} data-testid="sl-record-btn"
                className="mx-auto h-20 w-20 grid place-items-center rounded-full bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50 shadow-lg">
                {transcribing ? <Loader2 className="h-8 w-8 animate-spin" /> : <Mic className="h-8 w-8" />}
              </button>
            ) : (
              <button onClick={stopRec} data-testid="sl-stop-btn"
                className="mx-auto h-20 w-20 grid place-items-center rounded-full bg-destructive text-destructive-foreground animate-pulse shadow-lg">
                <Square className="h-7 w-7" />
              </button>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            {transcribing ? "Transcription en cours (Whisper)…" : recording ? "🎙️ En écoute — parlez en français, puis appuyez pour arrêter." : "Appuyez pour enregistrer votre réponse. Recommencez pour ajouter."}
          </p>
        </div>

        <div className="mt-4">
          <label className="text-xs font-semibold text-muted-foreground flex items-center gap-2 mb-1"><Type className="h-3.5 w-3.5" /> Your transcript ({words} mots) — you can edit before scoring</label>
          <textarea data-testid="sl-transcript" value={text} onChange={(e) => setText(e.target.value)} placeholder="Votre transcription apparaîtra ici. Vous pouvez aussi taper votre réponse."
            className="w-full min-h-[160px] p-5 rounded-2xl bg-card border border-border focus:outline-none focus:ring-2 focus:ring-ring leading-relaxed resize-y" />
          <p className="text-[11px] text-muted-foreground mt-1.5">Whisper often tidies up your French. For the most accurate level, edit this so it matches <span className="font-semibold">exactly what you said — mistakes and all</span>.</p>
        </div>

        <button onClick={submit} disabled={loading || recording || transcribing} data-testid="sl-submit"
          className="mt-4 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Correction en cours…</> : "Obtenir mon feedback"}
        </button>
        {loading && <p className="text-sm text-muted-foreground mt-2">L'IA estime votre niveau et prépare vos réécritures CLB5 et CLB7…</p>}
      </div>
    );
  }

  return (
    <div data-testid="speaking-lab-page">
      <h1 className="font-display text-4xl font-semibold">Speaking Lab</h1>
      <p className="text-muted-foreground mt-2">Record a timed TCF speaking task. We transcribe it with Whisper, estimate your level, and show how to reach the next one — with pronunciation flags.</p>
      <div className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Level estimate guided by the official TCF Canada criteria · <Volume2 className="h-3.5 w-3.5 text-primary" /> Voice powered by OpenAI Whisper.</div>

      <div className="grid sm:grid-cols-3 gap-5 mt-8">
        {tasks.map((t, i) => (
          <motion.button key={t.id} onClick={() => open(t)} data-testid={`sl-task-${t.id}`}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
            className="text-left rounded-2xl bg-card border border-border p-6 hover:shadow-lg hover:-translate-y-1 transition-all">
            <div className="h-11 w-11 rounded-xl emerald-surface grid place-items-center"><Mic className="h-5 w-5 text-white" /></div>
            <h3 className="mt-4 font-semibold">{t.title}</h3>
            <p className="text-sm text-muted-foreground mt-1 line-clamp-3">{t.prompt}</p>
            <div className="mt-3 flex items-center gap-2 text-xs font-semibold text-primary">
              <span className="px-2 py-0.5 rounded-full bg-accent">{t.target}</span>
              <span>~{Math.round(t.seconds / 60 * 10) / 10} min</span>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
